import React, { useEffect, useMemo, useState } from 'react';
import {
  Typography, DatePicker, Select, Button, Space, Table, Segmented, Tabs, Tag, Tooltip, message,
} from 'antd';
import dayjs from 'dayjs';
import api from '../api';
import { selectFilter } from '../utils/selectFilter';

// Recruit & Forecast > Headcount History: per month, team and job, from Employee
// Assignment History (from September 2026) -- see routes/headcount.py for how
// each figure is counted. Two views: Employed (In Training + In Production,
// from the first day) and Production (from graduation). Jobs default to Agent
// and Support Agent. Data Check lists the history that would make the numbers
// wrong.

const FIRST = dayjs('2026-09-01');
const DEFAULT_JOBS = ['agent', 'support agent'];
// OnLeave: taken off End like Terms; the current month only (Paycor's status
// now, no history).
const FIGURES = ['Start', 'Joins', 'TransfersIn', 'Terms', 'TransfersOut', 'OtherOut', 'OnLeave', 'End'];

export default function HeadcountPage() {
  const [range, setRange] = useState([FIRST, dayjs().startOf('month')]);
  const [view, setView]   = useState('employed');
  const [jobs, setJobs]   = useState([]);
  const [teams, setTeams] = useState([]);
  const [jobIds, setJobIds]   = useState([]);
  const [teamIds, setTeamIds] = useState([]);
  const [data, setData]       = useState(null);
  const [checks, setChecks]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab]         = useState('months');

  useEffect(() => {
    api.get('/api/teams', { params: { my_teams: 1, no_client_users: 1 } }).then((r) => setTeams(r.data || [])).catch(() => {});
    api.get('/api/jobs').then((r) => {
      const list = r.data || [];
      setJobs(list);
      setJobIds(list.filter((j) => DEFAULT_JOBS.includes((j.JobName || '').trim().toLowerCase())).map((j) => j.JobID));
    }).catch(() => {});
  }, []);

  const params = () => ({
    from_month: range[0].format('YYYY-MM'),
    to_month: range[1].format('YYYY-MM'),
    ...(jobIds.length ? { job_ids: jobIds.join(',') } : {}),
    ...(teamIds.length ? { team_ids: teamIds.join(',') } : {}),
  });

  const run = () => {
    setLoading(true);
    Promise.all([
      api.get('/api/headcount', { params: { ...params(), view } }),
      api.get('/api/headcount/check', { params: params() }),
    ])
      .then(([h, c]) => { setData(h.data); setChecks(c.data || []); })
      .catch((err) => message.error(err.response?.data?.error || 'Failed to load headcount.'))
      .finally(() => setLoading(false));
  };
  // Load once the default jobs are known, and again when the view changes.
  useEffect(() => { if (jobs.length) run(); }, [jobs.length, view]); // eslint-disable-line react-hooks/exhaustive-deps

  // Months across, figures down: one block per team and job (Start, New
  // Hires ... End as rows), closed by a Total block over them. Other Out only
  // when any month has some.
  const joinsLabel = view === 'production' ? 'Graduates' : 'New Hires';
  const FIGURE_LABEL = {
    Start: 'Start', Joins: joinsLabel, TransfersIn: 'Transfers In', Terms: 'Terms',
    TransfersOut: 'Transfers Out', OtherOut: 'Other Out', End: 'End',
    OnLeave: 'Currently On Leave',
  };
  const tableRows = useMemo(() => {
    if (!data) return [];
    const figures = FIGURES.filter((f) => f !== 'OtherOut' || data.rows.some((r) => r.OtherOut));
    const groups = [];
    data.rows.forEach((r) => {
      if (!groups.some((g) => g.TeamID === r.TeamID && g.JobID === r.JobID)) {
        groups.push({ TeamID: r.TeamID, JobID: r.JobID, TeamName: r.TeamName, JobName: r.JobName });
      }
    });
    groups.sort((a, b) => (a.TeamName || '').localeCompare(b.TeamName || '') || (a.JobName || '').localeCompare(b.JobName || ''));
    const block = (g, pick, isTotal) => figures.map((f, i) => {
      const row = { key: `${isTotal ? 'total' : `${g.TeamID}|${g.JobID}`}|${f}`, TeamName: g.TeamName, JobName: g.JobName,
        figure: f, first: i === 0, span: figures.length, isTotal };
      data.months.forEach((m) => {
        const rs = pick(m);
        // No figure for that month at all (On Leave before the current one): blank.
        row[m] = rs.length && rs.every((r) => r[f] == null) ? null : rs.reduce((s, r) => s + (r[f] || 0), 0);
      });
      return row;
    });
    return [
      ...groups.flatMap((g) => block(g, (m) => data.rows.filter((r) => r.Month === m && r.TeamID === g.TeamID && r.JobID === g.JobID), false)),
      ...(groups.length ? block({ TeamName: 'Total', JobName: '' }, (m) => data.rows.filter((r) => r.Month === m), true) : []),
    ];
  }, [data]);

  const strong = (r) => r.isTotal || r.figure === 'End';
  const num = (v, r) => (v == null ? <span style={{ color: '#bfbfbf' }}>—</span>
    : strong(r) ? <strong>{v}</strong> : v || <span style={{ color: '#bfbfbf' }}>0</span>);
  const groupCell = (r) => ({ rowSpan: r.first ? r.span : 0 });
  const columns = [
    { title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 170, onCell: groupCell,
      render: (v, r) => (r.isTotal ? <strong>{v}</strong> : v) },
    { title: 'Job', dataIndex: 'JobName', key: 'JobName', width: 150, onCell: groupCell },
    { title: '', dataIndex: 'figure', key: 'figure', width: 130,
      render: (v, r) => (strong(r) ? <strong>{FIGURE_LABEL[v]}</strong> : FIGURE_LABEL[v]) },
    ...(data?.months || []).map((m) => ({
      title: dayjs(`${m}-01`).format('MMM YYYY'), dataIndex: m, key: m, width: 100, align: 'right', render: num,
    })),
  ];

  const checkCols = [
    { title: 'Issue', dataIndex: 'Issue', key: 'Issue', width: 300,
      filters: [...new Set((checks || []).map((c) => c.Issue))].map((v) => ({ text: v, value: v })),
      onFilter: (v, r) => r.Issue === v, render: (v) => <Tag color="orange">{v}</Tag> },
    { title: 'Employee', dataIndex: 'DisplayName', key: 'DisplayName', width: 190,
      sorter: (a, b) => (a.DisplayName || '').localeCompare(b.DisplayName || '') },
    { title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 150, render: (v) => v || '—' },
    { title: 'Job', dataIndex: 'JobName', key: 'JobName', width: 140, render: (v) => v || '—' },
    { title: 'Status', dataIndex: 'Status', key: 'Status', width: 140, render: (v) => v || '—' },
    { title: 'Start', dataIndex: 'StartDate', key: 'StartDate', width: 105, render: (v) => v || '—' },
    { title: 'End', dataIndex: 'EndDate', key: 'EndDate', width: 105, render: (v) => v || '—' },
    { title: 'Detail', dataIndex: 'Detail', key: 'Detail', render: (v) => v || '' },
  ];

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>Headcount History</Typography.Title>

      <Space wrap style={{ marginBottom: 12 }}>
        <span>Months:</span>
        <DatePicker.RangePicker picker="month" value={range} allowClear={false}
          disabledDate={(d) => d.isBefore(FIRST, 'month') || d.isAfter(dayjs(), 'month')}
          onChange={(v) => v && setRange(v)} />
        <span>Job:</span>
        <Select mode="multiple" allowClear showSearch filterOption={selectFilter} placeholder="All jobs" style={{ minWidth: 240 }}
          maxTagCount="responsive" value={jobIds} onChange={setJobIds}
          options={jobs.map((j) => ({ value: j.JobID, label: j.JobName }))} />
        <span>Team:</span>
        <Select mode="multiple" allowClear showSearch filterOption={selectFilter} placeholder="All teams" style={{ minWidth: 220 }}
          maxTagCount="responsive" value={teamIds} onChange={setTeamIds}
          options={teams.map((t) => ({ value: t.ID, label: t.Name }))} />
        <Segmented value={view} onChange={setView}
          options={[
            { value: 'employed', label: (
              <Tooltip title="Everyone working, in training or in production, from their first day. New Hires are people who started; Terms include trainees who left.">
                <span>Employed</span>
              </Tooltip>
            ) },
            { value: 'production', label: (
              <Tooltip title="Only people who have graduated from training, counted from their graduation date. Graduates are people who graduated that month; Terms are graduated employees who left.">
                <span>Production</span>
              </Tooltip>
            ) },
          ]} />
        <Button type="primary" onClick={run} loading={loading}>Run</Button>
      </Space>
      {/* Head count, not FTE: routes/headcount.py counts assignment rows. */}
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: -4, marginBottom: 12 }}>
        Counts people, not FTE: each employee counts as 1 whatever their hours.{' '}
        {view === 'production'
          ? 'Production: only people who have graduated from training, from their graduation date.'
          : 'Employed: everyone working, in training or in production, from their first day.'}
      </Typography.Paragraph>

      <Tabs activeKey={tab} onChange={setTab} items={[
        { key: 'months', label: 'By Month', children: (
          <Table rowKey="key" size="small" bordered loading={loading} dataSource={tableRows} columns={columns}
            pagination={false} scroll={{ x: 'max-content' }}
            rowClassName={(r) => (r.isTotal ? 'headcount-total' : '')}
            locale={{ emptyText: 'No headcount for these filters.' }} />
        ) },
        { key: 'check', label: <span>Data Check{checks?.length ? <Tag color="orange" style={{ marginLeft: 6 }}>{checks.length}</Tag> : null}</span>, children: (
          <Table rowKey={(r, i) => `${r.Issue}|${r.UserID}|${r.StartDate}|${i}`} size="small" loading={loading}
            dataSource={checks || []} columns={checkCols} scroll={{ x: 'max-content' }}
            pagination={{ defaultPageSize: 50, hideOnSinglePage: true }}
            locale={{ emptyText: 'Nothing to fix.' }} />
        ) },
      ]} />
    </div>
  );
}
